import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, Loader2, LogIn, ShieldCheck, Users, Building2 } from "lucide-react";
import { Logo } from "../components/Logo";
import { useAuth } from "../context/AuthContext";
import { formatApiErrorDetail } from "../lib/api";
import { publicLink } from "../lib/domains";

export const dashboardPath = (user) =>
  user && user.role === "admin" ? "/admin/tenants" : "/dashboard";

const VARIANTS = {
  admin: {
    title: "SaaS Admin Login",
    sub: "Platform owner access — societies, tenants and collections across the whole network.",
    icon: ShieldCheck,
    testid: "login-heading-admin",
  },
  society: {
    title: "Society Login",
    sub: "Residents and committee members — your notices, requests and society dashboard.",
    icon: Users,
    testid: "login-heading-society",
  },
};

const TABS = [
  { to: "/login", label: "ERP Login", testid: "login-tab-erp" },
  { to: "/login/society", label: "Society", testid: "login-tab-society" },
  { to: "/login/admin", label: "SaaS Admin", testid: "login-tab-admin" },
];

export default function Login() {
  const { variant } = useParams();
  const v = VARIANTS[variant] || null;
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) navigate(dashboardPath(user));
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const data = await login(form.email, form.password);
      toast.success(`Welcome back, ${data.name.split(" ")[0]}`);
      navigate(dashboardPath(data));
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const VariantIcon = v ? v.icon : Building2;

  return (
    <div data-testid="login-page" className="relative flex min-h-screen items-center justify-center overflow-hidden bg-slate-50 px-5 py-16">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-40 left-1/2 h-[500px] w-[800px] -translate-x-1/2 rounded-full bg-gradient-to-b from-sky-100 via-blue-50 to-transparent blur-3xl" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_1px_1px,rgba(37,99,235,0.06)_1px,transparent_0)] [background-size:32px_32px]" />
      </div>

      <motion.div
        className="relative w-full max-w-md"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      >
        <a
          href={publicLink("/")}
          data-testid="login-back-home"
          className="mb-8 inline-flex items-center gap-2 text-sm font-medium text-slate-500 transition-colors hover:text-blue-600"
        >
          <ArrowLeft size={15} /> Back to synqsmc.com
        </a>

        <div className="card-soft p-8 shadow-lg shadow-slate-200/60 sm:p-10">
          <div className="flex items-center justify-between">
            <Logo size={44} />
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
              <VariantIcon size={20} />
            </span>
          </div>

          <h1 className="mt-8 text-2xl font-extrabold tracking-tight text-slate-900" data-testid={v ? v.testid : "login-heading"}>
            {v ? v.title : "Sign in to SynqSMC ERP"}
          </h1>
          <p className="mt-2 text-sm text-slate-500">
            {v ? v.sub : "ERP access for society admins, managers and committee members."}
          </p>

          <div className="mt-6 grid grid-cols-3 gap-1 rounded-full border border-slate-200 bg-slate-50 p-1" data-testid="login-variant-tabs">
            {TABS.map((t) => {
              const active = (variant || "") === t.to.replace("/login", "").replace("/", "");
              return (
                <Link
                  key={t.to}
                  to={t.to}
                  data-testid={t.testid}
                  className={`rounded-full py-2 text-center font-mono text-[10px] font-semibold uppercase tracking-[0.12em] transition-all duration-200 ${
                    active ? "bg-blue-600 text-white shadow-sm" : "text-slate-500 hover:text-slate-800"
                  }`}
                >
                  {t.label}
                </Link>
              );
            })}
          </div>

          <form onSubmit={submit} className="mt-7 space-y-5" data-testid="login-form">
            <div>
              <label htmlFor="auth-email" className="mb-2 block font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                Email
              </label>
              <input
                id="auth-email"
                type="email"
                data-testid="auth-email-input"
                className="input-field"
                placeholder="you@example.com"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
              />
            </div>
            <div>
              <label htmlFor="auth-password" className="mb-2 block font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                Password
              </label>
              <input
                id="auth-password"
                type="password"
                data-testid="auth-password-input"
                className="input-field"
                placeholder="••••••••"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                required
                minLength={6}
              />
            </div>

            {error && (
              <p data-testid="auth-error" className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={busy}
              data-testid="auth-submit-button"
              className="btn-primary w-full !py-3.5 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {busy ? <Loader2 size={16} className="animate-spin" /> : <LogIn size={16} />}
              {busy ? "Signing in…" : "Sign in"}
            </button>
          </form>

          <div className="mt-6 flex items-center gap-2 rounded-lg bg-slate-50 px-4 py-3" data-testid="login-security-note">
            <ShieldCheck size={15} className="shrink-0 text-emerald-600" />
            <p className="text-xs text-slate-500">
              Secured with encrypted sessions and brute-force lockout protection.
            </p>
          </div>

          <a
            href={publicLink("/")}
            data-testid="login-back-button"
            className="btn-ghost mt-5 w-full !py-3"
          >
            <ArrowLeft size={15} /> Back to synqsmc.com
          </a>
        </div>

        <p className="mt-6 text-center font-mono text-[11px] uppercase tracking-[0.18em] text-slate-400" data-testid="login-domain-note">
          erp.synqsmc.com
        </p>
      </motion.div>
    </div>
  );
}
